/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */

import React, { useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useSearchParams,
  matchPath,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { Conditional, Loader } from '@cashfree-intl/coherent';
import {
  AuthProvider as RBACAuthProvider,
  ProductName,
} from '@cashfree-intl/auth';

// Utils
import { getBrowserData, redirectToCommonAuthLogin } from 'utils/common';

// Constants
import {
  TEST,
  GLOBAL_SIDEBAR_MAPPING,
  ONBOARDING_ROUTES as AUTH_ROUTES,
  EXCLUDED_PAGE_LIST_FOR_MOBILE_APP_PROMOTION_PAGE,
  PG_DASHBOARD_SUMMARY_PATH,
} from 'utils/constants';

// Providers
import { AuthProvider } from 'providers/AuthProvider';
import { ProductProvider } from 'providers/ProductProvider';
import { MerchantDetailsProvider } from 'providers/MerchantDetailsProvider';
import NotificationProvider from 'providers/NotificationProvider';
import UserDetailsProvider from 'providers/UserDetailsProvider';
import { ProductTourProvider } from 'providers/ProductTourProvider';

// Containers
import VerifyMerchantEmail from 'containers/VerifyMerchantEmail/Loadable';
import AuthenticatedRoute from 'containers/AuthenticatedRoute';
import Flowwise from 'containers/Flowwise/Loadable';
import FlowwiseLayout from 'containers/FlowwiseLayout/Loadable';
import BaseLayout from 'containers/BaseLayout';
import MainLayout from 'containers/MainLayout';
import DayZero from 'containers/CheckoutJourney';

// Pages
import ShareableReportLandingPage from 'containers/ShareableReportLandingPage/Loadable';
import DayZeroLayout from 'containers/CheckoutJourney/Layout';

import { INSIGHTS_AI_PATH } from 'containers/FlowwiseLayout/constants';

import AuthPage from '../../pages/AuthPage/loadable';
import IframePage from '../../pages/IframePage/loadable';
import LandingPage from '../../pages/LandingPage/loadable';
import NotFoundPage from '../../pages/NotFoundPage/loadable';
import PGPage from '../../pages/PGPage/loadable';
import ValidatePage from '../../pages/ValidatePage/loadable';
import SarPage from '../../pages/SarPage/loadable';
import TncContainerPage from '../../pages/TncContainerPage/loadable';

// Components
import UserGuidanceBanner from '../../components/UserGuidanceBanner';

import { basename } from '../../store';
import reducer from './reducer';
import { appSelector } from './selectors';

const RedirectToCommonAuthLogin = () => {
  redirectToCommonAuthLogin();
  return null;
};

export default function App() {
  useInjectReducer({ key: 'app', reducer });
  const appData = useSelector(appSelector());
  const [searchParams] = useSearchParams();

  const [showUserGuidance, resetShowUserGuidance] = useState(true);
  const osName = getBrowserData().os.name;

  const isCurrentPageExcludedFromMobilePrmotionPage = EXCLUDED_PAGE_LIST_FOR_MOBILE_APP_PROMOTION_PAGE.some(
    path => matchPath({ path: `${basename}${path}` }, window.location.pathname),
  );

  const envObj = process.env;

  const getEnvironmentParam = () =>
    searchParams.get('env') === TEST ? 'test' : 'prod';

  return (
    <AuthProvider>
      <Conditional
        if={showUserGuidance && !isCurrentPageExcludedFromMobilePrmotionPage}
      >
        <UserGuidanceBanner type={osName} isShellV2={appData?.isShellV2} />
      </Conditional>
      <Routes>
        <Route path="*" element={<NotFoundPage />} />
        <Route path="/" element={<Navigate to={`${basename}signup`} />} />
        <Route path={`${basename}`}>
          {/* This default route redirects merchant.cashfree.com/merchants to merchant.cashfree.com/merchants/login */}
          <Route index element={<RedirectToCommonAuthLogin />} />
          <Route path="login" element={<RedirectToCommonAuthLogin />} />
          {AUTH_ROUTES.map(path => (
            <Route key={path} path={path} element={<AuthPage path={path} />} />
          ))}
          {Object.keys(GLOBAL_SIDEBAR_MAPPING).map(path => (
            <Route
              key={path}
              path={path}
              element={<IframePage path={path} />}
            />
          ))}
          <Route
            path="landing"
            element={
              <AuthenticatedRoute>
                <NotificationProvider>
                  <MerchantDetailsProvider>
                    <UserDetailsProvider>
                      <ProductTourProvider>
                        <ProductProvider>
                          <BaseLayout>
                            {({
                              isTest,
                              toggleEnv,
                              setIsTest,
                              setInsightsAiIconDisplay,
                            }) => (
                              <LandingPage
                                isTest={isTest}
                                toggleEnv={toggleEnv}
                                setIsTest={setIsTest}
                                setInsightsAiIconDisplay={
                                  setInsightsAiIconDisplay
                                }
                              />
                            )}
                          </BaseLayout>
                        </ProductProvider>
                      </ProductTourProvider>
                    </UserDetailsProvider>
                  </MerchantDetailsProvider>
                </NotificationProvider>
              </AuthenticatedRoute>
            }
          />
          <Route path="validate/:merchantToken" element={<ValidatePage />} />

          <Route path="pg/flowwise-v2">
            <Route
              index
              element={
                <Navigate
                  to={`${basename}${
                    envObj.FLOWWISE_BASE_PATH
                  }${INSIGHTS_AI_PATH}?env=${getEnvironmentParam()}`}
                />
              }
            />
            <Route
              path="*"
              element={
                <AuthenticatedRoute>
                  <MerchantDetailsProvider>
                    <UserDetailsProvider>
                      <ProductProvider>
                        <RBACAuthProvider
                          product={ProductName.PG}
                          appEnv={envObj.APP_ENV}
                          loader={<Loader />}
                        >
                          <NotificationProvider>
                            <FlowwiseLayout>
                              {active => <Flowwise active={active} />}
                            </FlowwiseLayout>
                          </NotificationProvider>
                        </RBACAuthProvider>
                      </ProductProvider>
                    </UserDetailsProvider>
                  </MerchantDetailsProvider>
                </AuthenticatedRoute>
              }
            />
          </Route>

          <Route path="pg">
            {['', '/'].map(() => (
              <Route
                path=""
                element={
                  <Navigate
                    to={`${basename}${PG_DASHBOARD_SUMMARY_PATH}${
                      searchParams.get('env') === TEST
                        ? `?env=test`
                        : '?env=prod'
                    }`}
                  />
                }
              />
            ))}
            <Route
              path=":pathname/*"
              element={
                <AuthenticatedRoute>
                  <MerchantDetailsProvider>
                    <UserDetailsProvider>
                      <RBACAuthProvider
                        product={ProductName.PG}
                        appEnv={envObj.APP_ENV}
                        loader={<Loader />}
                      >
                        <NotificationProvider>
                          <MainLayout>
                            {displayHeaderText => (
                              <PGPage
                                displayHeaderText={displayHeaderText}
                                os={osName}
                              />
                            )}
                          </MainLayout>
                        </NotificationProvider>
                      </RBACAuthProvider>
                    </UserDetailsProvider>
                  </MerchantDetailsProvider>
                </AuthenticatedRoute>
              }
            />
          </Route>
          <Route
            path="sar"
            element={<SarPage resetShowUserGuidance={resetShowUserGuidance} />}
          />
          <Route
            path="tnc"
            element={
              <TncContainerPage resetShowUserGuidance={resetShowUserGuidance} />
            }
          />
          <Route path="verify-email" element={<VerifyMerchantEmail />} />
          <Route path="reports/*" element={<ShareableReportLandingPage />} />

          <Route
            path="checkout-journey/*"
            element={
              <AuthenticatedRoute>
                <NotificationProvider>
                  <MerchantDetailsProvider>
                    <UserDetailsProvider>
                      <ProductTourProvider>
                        <ProductProvider>
                          <DayZeroLayout>
                            <DayZero />
                          </DayZeroLayout>
                        </ProductProvider>
                      </ProductTourProvider>
                    </UserDetailsProvider>
                  </MerchantDetailsProvider>
                </NotificationProvider>
              </AuthenticatedRoute>
            }
          />
        </Route>
      </Routes>
    </AuthProvider>
  );
}
